/**
 * Edition context — the runtime separation axis for ChooseChow vs ChowDash.
 *
 * ChooseChow and ChowDash are distinct legal entities sharing one codebase (see
 * docs/edition_separation.md). The active edition is resolved in this order:
 *
 * 1. The mount prefix in the path (`/chowdash` → ChowDash), which also locks it.
 * 2. A `?edition=` (or `?app=`) query param, for driving the root mount into
 *    either product's context during development.
 * 3. The edition stored by the last switch in this session.
 * 4. The build's `VITE_APP_EDITION`.
 *
 * The resolved edition rides along with auth on every API call (see lib/api.ts)
 * as the `X-Chow-Edition` header. Later this is augmented by a backend
 * `app_scope` and per-edition native builds.
 */
import { basePathFor, editionForBasePath, type Edition } from './editionPath'

// The edition identity and its path mounts live in editionPath, which stays
// import-free so vite.config.ts can read the mount table from Node. Re-exported
// here because this is where the rest of the app already imports it from.
export type { Edition } from './editionPath'

export const EDITIONS: readonly Edition[] = ['choosechow', 'chowdash', 'chow']

export const isEdition = (value: unknown): value is Edition =>
  typeof value === 'string' && (EDITIONS as readonly string[]).includes(value)

const STORAGE_KEY = 'chow_edition'
const QUERY_KEYS = ['edition', 'app']

const buildDefault: Edition = isEdition(import.meta.env.VITE_APP_EDITION)
  ? import.meta.env.VITE_APP_EDITION
  : 'chow'

/** Accept a few friendly aliases so the test query param is forgiving. */
const normalize = (value: string | null | undefined): Edition | null => {
  if (!value) return null
  switch (value.toLowerCase()) {
    case 'choosechow':
    case 'choose_chow':
    case 'choose':
      return 'choosechow'
    case 'chowdash':
    case 'chow_dash':
    case 'dash':
    case 'rider':
      return 'chowdash'
    case 'chow':
    case 'both':
    case 'combined':
      return 'chow'
    default:
      return null
  }
}

export const editionFromSearch = (search: string): Edition | null => {
  const params = new URLSearchParams(search)
  for (const key of QUERY_KEYS) {
    const found = normalize(params.get(key))
    if (found) return found
  }
  return null
}

const readStored = (): Edition | null => {
  try {
    return normalize(sessionStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

/**
 * The mount prefix the app was loaded under, resolved once at boot.
 *
 * React Router is given this as its basename (see main.tsx), so the path is
 * fixed for the lifetime of the document: a link rendered inside the mount can
 * only ever produce another prefixed path.
 */
const lockedBasePath =
  typeof window !== 'undefined' ? basePathFor(window.location.pathname) : ''

/** The edition this mount is locked to, or `null` on the switchable root mount. */
const lockedEdition = editionForBasePath(lockedBasePath)

// Module-level cache so non-React code (the API client) can read the active
// edition synchronously. The provider keeps this in sync with the URL.
let current: Edition =
  lockedEdition ??
  (typeof window !== 'undefined' ? editionFromSearch(window.location.search) : null) ??
  readStored() ??
  buildDefault

export const getEdition = (): Edition => current

/**
 * True when the mount prefix owns the edition.
 *
 * A locked mount must not change edition: the URL is the product identity, so a
 * query param or the switcher that changes it would hand the client a rider app
 * that says "ChooseChow" in the address bar. `setActiveEdition` therefore
 * ignores the request, and callers hide the affordance.
 */
export const isEditionLocked = (): boolean => lockedEdition !== null

/** The mount prefix this document is served under (`''` at the root). */
export const getBasePath = (): string => lockedBasePath

export const setActiveEdition = (edition: Edition): void => {
  if (lockedEdition) return
  current = edition
  try {
    sessionStorage.setItem(STORAGE_KEY, edition)
  } catch {
    /* sessionStorage unavailable — module cache is enough */
  }
}

export type Surface = 'order' | 'dash'

/** Glyph keys resolved to icons by the AppDock (keeps this module React-free). */
export type DockIconKey =
  | 'home'
  | 'chef'
  | 'dish'
  | 'market'
  | 'wallet'
  | 'inbox'
  | 'help'
  | 'cart'
  | 'profile'
  | 'offers'
  | 'active'
  | 'earnings'
  | 'safety'
export interface DockTab {
  path: string
  label: string
  icon: DockIconKey
}

export interface EditionAccent {
  brand: string
  brandSoft: string
}

export interface EditionProfile {
  /** Brand-facing product name for the shipped app. */
  label: string
  tagline: string
  /** Two-tone wordmark parts ("choose" + "chow"). */
  wordmark: { lead: string; tail: string }
  /** Accent that restains the ui-minimal theme for this edition. */
  accent: EditionAccent
  /** Full-bleed page-header gradient. */
  headerGradient: string
  /** Bottom-dock tabs / routes. */
  dockTabs: readonly DockTab[]
  /** Profile classes the edition's auth context may carry. */
  authClasses: readonly string[]
  /** Home surfaces this edition exposes (legacy combined home). */
  surfaces: readonly Surface[]
}

const chooseChowTabs: readonly DockTab[] = [
  { path: '/', label: 'Home', icon: 'home' },
  { path: '/kitchens', label: 'Kitchens', icon: 'chef' },
  { path: '/dishes', label: 'Dishes', icon: 'dish' },
  { path: '/profile', label: 'Profile', icon: 'profile' },
]

const chowDashTabs: readonly DockTab[] = [
  { path: '/', label: 'Home', icon: 'home' },
  { path: '/jobs', label: 'Jobs', icon: 'active' },
  { path: '/earnings', label: 'Earnings', icon: 'earnings' },
  { path: '/inbox', label: 'Inbox', icon: 'inbox' },
]

export const EDITION_PROFILE: Record<Edition, EditionProfile> = {
  choosechow: {
    label: 'ChooseChow',
    tagline: 'Homemade food, ready for pickup',
    wordmark: { lead: 'choose', tail: 'chow' },
    accent: { brand: '#E61445', brandSoft: 'rgba(230, 20, 69, 0.11)' },
    headerGradient: '#A80F34',
    dockTabs: chooseChowTabs,
    authClasses: ['customer', 'chef', 'market_vendor'],
    surfaces: ['order'],
  },
  chowdash: {
    label: 'ChowDash',
    tagline: 'Earn on every drop',
    wordmark: { lead: 'chow', tail: 'dash' },
    accent: { brand: '#0f7d6b', brandSoft: 'rgba(15, 125, 107, 0.13)' },
    headerGradient: '#0d5f50',
    dockTabs: chowDashTabs,
    authClasses: ['rider', 'ops', 'support'],
    surfaces: ['dash'],
  },
  chow: {
    label: 'Chow',
    tagline: 'Network · dev',
    wordmark: { lead: 'Choose', tail: 'Chow' },
    accent: { brand: '#E61445', brandSoft: 'rgba(230, 20, 69, 0.11)' },
    headerGradient: '#A80F34',
    dockTabs: chooseChowTabs,
    authClasses: ['customer', 'rider'],
    surfaces: ['order', 'dash'],
  },
}
